/**
 * Turn a failed action into a sentence worth showing a technician.
 *
 * The three cases are genuinely different and must not collapse into one
 * "something went wrong":
 *
 * - **We never reached the server.** `fetch` rejects with a TypeError, so the
 *   failure is not an {@link ApiError} at all. This is the only case that is
 *   honestly a connection problem, and the only one where retrying the same
 *   tap is the right advice.
 * - **The server answered and said no.** 4xx carries an RFC 7807 `detail`,
 *   which the shared client already unpacks into `.message` — "a sentence
 *   written for the operator". Someone else claimed the task, the WO moved
 *   state, the task is not yours. We show what the server wrote rather than
 *   guessing, because it knows which of those it was and we do not.
 * - **The server broke.** 5xx `detail` is written for whoever reads the logs,
 *   not for a tech holding a torque wrench, so we substitute our own.
 *
 * Getting this wrong has a cost on the floor: telling a tech "connection
 * issue" when the truth is "your colleague started this task 20 seconds ago"
 * sends them to go and check the wifi over something the wifi did not do.
 */
import { ApiError } from '@/apis/apiClient'
import type { Translations } from '@/i18n/translations/de'

export function actionErrorMessage(error: unknown, t: Translations): string {
  if (!(error instanceof ApiError)) return t.errors.offline
  if (error.status >= 500) return t.errors.server
  // Trust the server's own words when it bothered to write any.
  return error.message?.trim() ? error.message : t.errors.rejected
}

/** Only a failure we never got an answer to is worth offering a retry on — a
 *  409 will keep being a 409 however many times it is tapped. */
export function isRetryable(error: unknown): boolean {
  return !(error instanceof ApiError) || error.status >= 500
}
